import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Package,
    Calendar,
    Settings,
    FileText,
    Camera,
    Paperclip,
    Save,
    Plus,
    X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { Asset } from '../../types/asset';
import { User } from '../../types';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const MAINTENANCE_TYPES = [
    'Oil Change',
    'Major Service',
    'Brake Check',
    'Routine Inspection',
    'Hardware Cleaning',
    'Software Update',
    'Battery Replacement',
    'Tyre Rotation',
    'Fluid Check',
    'Emergency Repair',
    'Preventive Maintenance',
    'Calibration',
    'Safety Inspection'
];

const RECURRENCE_INTERVALS = [
    'Every 3 months',
    'Every 6 months',
    'Every 12 months',
    'Every 5,000 km',
    'Every 10,000 km',
    'Every 15,000 km',
    'Every 20,000 km',
    'Every 500 hours',
    'Every 1,000 hours',
    'As needed'
];

const ScheduleMaintenance: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const [asset, setAsset] = useState<Asset | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [partInput, setPartInput] = useState('');
    const [partsList, setPartsList] = useState<string[]>([]);

    const [formData, setFormData] = useState({
        type: '',
        recurrenceInterval: '',
        scheduledDate: new Date().toISOString().split('T')[0],
        scheduledTime: '',
        completedDate: '',
        nextInspectionDue: '',
        odometerReading: '',
        vendor: '',
        technicianName: '',
        location: '',
        cost: '',
        description: '',
        approvedBy: ''
    });

    const [files, setFiles] = useState<{ images: File[], documents: File[] }>({
        images: [],
        documents: []
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [assetRes, approversRes] = await Promise.all([
                    api.get<Asset>(`/api/assets/${id}`),
                    api.get<{ users: User[] }>('/api/assets/repair-approvers')
                ]);
                setAsset(assetRes);
                // Fallback in case endpoint returns array or object with users/approvers
                if (Array.isArray(approversRes)) {
                    setUsers(approversRes);
                } else if (approversRes && approversRes.users) {
                    setUsers(approversRes.users);
                } else if (approversRes && (approversRes as any).approvers) {
                    setUsers((approversRes as any).approvers);
                } else {
                    setUsers([]);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load asset details');
                navigate(`/assets/${id}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, navigate]);

    if (!ability.can(ACTIONS.CREATE, RESOURCES.ASSET_MAINTENANCE)) {
        return <AccessRestricted />;
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const updates: any = { [name]: value };

            if (name === 'completedDate' && value && asset?.inspectionCadenceNumber) {
                const dateObj = new Date(value);
                const unit = asset.inspectionCadenceUnit || 'days';
                if (unit === 'days') dateObj.setDate(dateObj.getDate() + asset.inspectionCadenceNumber);
                else if (unit === 'weeks') dateObj.setDate(dateObj.getDate() + (asset.inspectionCadenceNumber * 7));
                else if (unit === 'months') dateObj.setMonth(dateObj.getMonth() + asset.inspectionCadenceNumber);
                updates.nextInspectionDue = dateObj.toISOString().split('T')[0];
            } else if (name === 'completedDate' && !value) {
                updates.nextInspectionDue = '';
            }

            return { ...prev, ...updates };
        });
    };

    const addPart = () => {
        const trimmed = partInput.trim();
        if (trimmed && !partsList.includes(trimmed)) {
            setPartsList(prev => [...prev, trimmed]);
            setPartInput('');
        }
    };

    const removePart = (part: string) => {
        setPartsList(prev => prev.filter(p => p !== part));
    };

    const handlePartKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addPart();
        }
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'images' | 'documents') => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);

            const allowedExtensions = type === 'images' 
                ? ['jpg', 'jpeg', 'png', 'gif']
                : ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
                
            const invalidFiles = newFiles.filter(file => {
                const ext = file.name.split('.').pop()?.toLowerCase();
                return !ext || !allowedExtensions.includes(ext);
            });
            
            if (invalidFiles.length > 0) {
                const firstInvalid = invalidFiles[0];
                const ext = firstInvalid.name.split('.').pop()?.toLowerCase() || 'unknown';
                toast.error(`File extension '.${ext}' is not allowed for file '${firstInvalid.name}'.`);
                // clear the input
                e.target.value = '';
                return;
            }

            setFiles(prev => ({
                ...prev,
                [type]: [...prev[type], ...newFiles]
            }));
        }
    };

    const removeFile = (type: 'images' | 'documents', indexToRemove: number) => {
        setFiles(prev => ({
            ...prev,
            [type]: prev[type].filter((_, index) => index !== indexToRemove)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.type) {
            toast.error('Please select a Maintenance Type');
            return;
        }
        if (!formData.scheduledDate) {
            toast.error('Please select a Scheduled Date');
            return;
        }

        if (files.images.length === 0 && files.documents.length === 0) {
            toast.error('Please upload at least one image or document');
            return;
        }

        try {
            setSubmitting(true);
            const data = new FormData();

            Object.entries(formData).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    data.append(key, value);
                }
            });

            // Serialize parts list as comma-separated string
            if (partsList.length > 0) {
                data.append('partsReplaced', partsList.join(', '));
            }

            [...files.images, ...files.documents].forEach(file => {
                data.append('attachments', file);
            });

            await api.post(`/api/assets/${id}/maintenance`, data, { skipErrorToast: true });

            toast.success('Maintenance record scheduled successfully');
            navigate(`/assets/${id}`);
        } catch (error: any) {
            console.error('Error saving maintenance:', error);
            toast.error(error.message || 'Failed to schedule maintenance');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !asset) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-8">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 flex-shrink-0"
                    onClick={() => navigate(`/assets/${id}`)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Schedule Asset Maintenance</h1>
                    <p className="text-gray-500 text-sm font-medium mt-1">
                        Create maintenance record for {asset.name} ({asset.tag})
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col xl:flex-row gap-6">
                {/* Main Content Area */}
                <div className="flex-1 space-y-6">
                    {/* Asset Information block */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <h2 className="text-base font-bold text-gray-900 mb-6 font-poppins">Asset Information</h2>
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100 shadow-sm shrink-0 overflow-hidden">
                                {asset.image ? (
                                    <img src={asset.image} alt={asset.name} className="w-full h-full object-cover" />
                                ) : (
                                    <Package size={24} />
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 leading-tight">{asset.name}</h3>
                                <div className="text-sm font-medium text-gray-500 mt-1 mb-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                    <span>{asset.tag}</span>
                                    <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-gray-300"></span>
                                    <span>{asset.type}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="px-2.5 py-0.5 rounded-lg border-gray-100 text-[10px] font-bold uppercase tracking-wider bg-gray-50">
                                        {asset.status}
                                    </Badge>
                                    <span className="text-xs font-medium text-gray-500">{asset.brand} {asset.model}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Scheduling Details */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <Calendar className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Scheduling & Status</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Set maintenance schedule and current status</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Maintenance Type <span className="text-red-500">*</span></label>
                                <Select value={formData.type} onValueChange={(val) => handleSelectChange('type', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12">
                                        <SelectValue placeholder="Select maintenance type..." />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100 max-h-[300px]">
                                        {MAINTENANCE_TYPES.map(type => (
                                            <SelectItem key={type} value={type} className="py-2.5">{type}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Recurrence Interval</label>
                                <Select value={formData.recurrenceInterval} onValueChange={(val) => handleSelectChange('recurrenceInterval', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12">
                                        <SelectValue placeholder="Select recurrence..." />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100 max-h-[300px]">
                                        {RECURRENCE_INTERVALS.filter(interval => 
                                            asset?.type === 'Vehicle' ? true : !interval.includes('km')
                                        ).map(interval => (
                                            <SelectItem key={interval} value={interval} className="py-2.5">{interval}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Scheduled Date <span className="text-red-500">*</span></label>
                                <Input
                                    type="date"
                                    name="scheduledDate"
                                    max="9999-12-31"
                                    value={formData.scheduledDate}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Completed Date</label>
                                <Input
                                    type="date"
                                    name="completedDate"
                                    max="9999-12-31"
                                    value={formData.completedDate}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer mt-[36px]">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                        checked={!!formData.completedDate}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const val = new Date().toISOString().split('T')[0];
                                                handleChange({ target: { name: 'completedDate', value: val } } as any);
                                            } else {
                                                handleChange({ target: { name: 'completedDate', value: '' } } as any);
                                            }
                                        }}
                                    />
                                    <span className="text-sm font-bold text-gray-900">Mark as completed</span>
                                </label>
                            </div>

                            {asset?.inspectionCadenceNumber && formData.completedDate && (
                                <div className="space-y-2 col-span-1 md:col-span-2 bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                                    <label className="text-xs font-bold text-gray-900 flex items-center justify-between">
                                        Next Inspection Due Date
                                        <Badge variant="outline" className="px-2 font-bold text-[9px] uppercase border-blue-200 text-blue-600 bg-white">Auto-calculated</Badge>
                                    </label>
                                    <Input
                                        type="date"
                                        name="nextInspectionDue"
                                        max="9999-12-31"
                                        value={formData.nextInspectionDue || ''}
                                        onChange={handleChange}
                                        className="rounded-2xl border-white bg-white h-12 px-4 shadow-sm"
                                        required
                                    />
                                    <p className="text-[10px] text-gray-500 font-medium tracking-wide">
                                        Based on inspection cadence of {asset.inspectionCadenceNumber} {asset.inspectionCadenceUnit}. You can manually adjust this date if needed.
                                    </p>
                                </div>
                            )}

                            {asset.type === 'Vehicle' && (
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-900">Odometer Reading (km)</label>
                                    <Input
                                        name="odometerReading"
                                        placeholder="e.g. 45000"
                                        type="number"
                                        value={formData.odometerReading}
                                        onChange={handleChange}
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Service Details */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <Settings className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Service Details</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Provide details about the maintenance work</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Time</label>
                                <Input
                                    type="time"
                                    name="scheduledTime"
                                    value={formData.scheduledTime}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4 w-full"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Vendor</label>
                                <Input
                                    name="vendor"
                                    placeholder="e.g. Ford Service Centre, Internal IT Team"
                                    value={formData.vendor}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Technician Name</label>
                                <Input
                                    name="technicianName"
                                    placeholder="e.g. Mark Thompson"
                                    value={formData.technicianName}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Location</label>
                                <Input
                                    name="location"
                                    placeholder="e.g. On-site, Service Centre Address"
                                    value={formData.location}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>
                            <div className="space-y-2 relative">
                                <label className="text-xs font-bold text-gray-900">Cost (excluding taxes)</label>
                                <div className="absolute left-4 top-[38px] text-gray-500 font-medium">$</div>
                                <Input
                                    name="cost"
                                    type="number"
                                    placeholder="0.00"
                                    value={formData.cost}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 pl-8 pr-4"
                                />
                            </div>

                            <div className="col-span-1 md:col-span-2 space-y-2">
                                <label className="text-xs font-bold text-gray-900">Description</label>
                                <Textarea
                                    name="description"
                                    placeholder="Provide detailed description of the maintenance work performed..."
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[100px] p-4 resize-none"
                                />
                            </div>

                            <div className="col-span-1 md:col-span-2 space-y-2">
                                <label className="text-xs font-bold text-gray-900">Parts Replaced</label>
                                <div className="relative">
                                    <Input
                                        placeholder="Enter part name..."
                                        value={partInput}
                                        onChange={(e) => setPartInput(e.target.value)}
                                        onKeyDown={handlePartKeyDown}
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4 pr-12"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        className="absolute right-2 top-2 h-8 w-8 p-0 rounded-lg hover:bg-gray-100 bg-white border border-gray-100"
                                        onClick={addPart}
                                    >
                                        <Plus size={16} />
                                    </Button>
                                </div>
                                {partsList.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {partsList.map((part) => (
                                            <span
                                                key={part}
                                                className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-lg"
                                            >
                                                {part}
                                                <button
                                                    type="button"
                                                    onClick={() => removePart(part)}
                                                    className="text-gray-400 hover:text-gray-700 transition-colors"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Approved By</label>
                                <Select value={formData.approvedBy} onValueChange={(val) => handleSelectChange('approvedBy', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12">
                                        <SelectValue placeholder="Select approver..." />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100 max-h-[300px]">
                                        {users.map(user => (
                                            <SelectItem key={user.id} value={user.id.toString()} className="py-2.5">
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-gray-900">{user.name}</span>
                                                    <span className="text-[10px] text-gray-500">{user.jobRole || 'No role'}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Attachments */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <FileText className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Attachments <span className="text-red-500">*</span></h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Upload images and documents related to the maintenance (at least one file required)</p>
                            </div>
                        </div>
                        <div className="space-y-6">
                            {/* Images Upload */}
                            <div className="space-y-3">
                                <label className="text-sm font-bold text-gray-900 flex items-center justify-between">
                                    <span>Images <span className="text-gray-400 font-medium">(max 10)</span></span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/jpeg,image/png,image/gif"
                                        onChange={(e) => handleFileChange(e, 'images')}
                                        className="hidden"
                                        id="image-upload"
                                    />
                                    <label
                                        htmlFor="image-upload"
                                        className="flex items-center w-full min-h-[48px] px-4 rounded-2xl border border-gray-100 bg-gray-50/50 hover:bg-gray-100 transition-colors cursor-pointer group"
                                    >
                                        <div className="bg-white px-3 py-1.5 rounded-lg border border-gray-100 text-xs font-bold text-gray-700 mr-3 shadow-sm group-hover:shadow transition-shadow">
                                            Choose Files
                                        </div>
                                        <span className="text-sm font-medium text-gray-500 truncate">
                                            {files.images.length > 0 ? `${files.images.length} file(s) chosen` : 'No file chosen'}
                                        </span>
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500">Supported formats: JPG, PNG, GIF. Max file size: 5MB each.</p>
                                {/* Preview Images */}
                                {files.images.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {files.images.map((file, i) => (
                                            <div key={i} className="flex items-center gap-2 bg-gray-50 pl-3 pr-2 py-1.5 rounded-lg border border-gray-100">
                                                <Camera size={14} className="text-blue-500" />
                                                <span className="text-xs font-medium text-gray-700 truncate max-w-[150px]">{file.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile('images', i)}
                                                    className="p-1 hover:bg-gray-200 rounded-md transition-colors text-gray-400 hover:text-red-500 ml-1"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Documents Upload */}
                            <div className="space-y-3">
                                <label className="text-sm font-bold text-gray-900 flex items-center justify-between">
                                    <span>Documents <span className="text-gray-400 font-medium">(max 10)</span></span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                                        onChange={(e) => handleFileChange(e, 'documents')}
                                        className="hidden"
                                        id="doc-upload"
                                    />
                                    <label
                                        htmlFor="doc-upload"
                                        className="flex items-center w-full min-h-[48px] px-4 rounded-2xl border border-gray-100 bg-gray-50/50 hover:bg-gray-100 transition-colors cursor-pointer group"
                                    >
                                        <div className="bg-white px-3 py-1.5 rounded-lg border border-gray-100 text-xs font-bold text-gray-700 mr-3 shadow-sm group-hover:shadow transition-shadow">
                                            Choose Files
                                        </div>
                                        <span className="text-sm font-medium text-gray-500 truncate">
                                            {files.documents.length > 0 ? `${files.documents.length} file(s) chosen` : 'No file chosen'}
                                        </span>
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500">Supported formats: PDF, DOC, DOCX, XLS, XLSX, TXT. Max file size: 10MB each.</p>
                                {/* Preview Documents */}
                                {files.documents.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {files.documents.map((file, i) => (
                                            <div key={i} className="flex items-center gap-2 bg-gray-50 pl-3 pr-2 py-1.5 rounded-lg border border-gray-100">
                                                <Paperclip size={14} className="text-emerald-500" />
                                                <span className="text-xs font-medium text-gray-700 truncate max-w-[150px]">{file.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile('documents', i)}
                                                    className="p-1 hover:bg-gray-200 rounded-md transition-colors text-gray-400 hover:text-red-500 ml-1"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Sticky Area */}
                <div className="w-full xl:w-[350px] flex-shrink-0">
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm sticky top-6">
                        <div className="flex items-center gap-3 mb-8">
                            <Settings className="text-gray-400" size={20} />
                            <h2 className="text-base font-bold text-gray-900 font-poppins">Maintenance Summary</h2>
                        </div>

                        <div className="space-y-6 mb-8">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Asset</span>
                                <p className="text-sm font-medium text-gray-900">{asset.name} ({asset.tag})</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Type</span>
                                <p className="text-sm font-medium text-gray-900">{formData.type || 'Not specified'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Vendor</span>
                                <p className="text-sm font-medium text-gray-900 md:truncate">{formData.vendor || 'Not specified'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Status</span>
                                {formData.completedDate ? (
                                    <div className="flex items-center gap-1.5 text-green-600 font-bold text-sm">
                                        <Calendar size={14} /> Completed
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1.5 text-orange-500 font-bold text-sm">
                                        <Calendar size={14} /> Scheduled
                                    </div>
                                )}
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Cost</span>
                                <p className="text-sm font-medium text-gray-900">{formData.cost ? `$${Number(formData.cost).toFixed(2)}` : 'Not set'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Parts</span>
                                <p className="text-sm font-medium text-gray-900">{partsList.length} listed</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Attachments</span>
                                <p className="text-sm font-medium text-gray-900">{files.images.length} images, {files.documents.length} documents</p>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full rounded-2xl bg-slate-900 hover:bg-slate-800 text-white h-12 font-bold mb-6 gap-2 text-sm shadow-md transition-all"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Save Maintenance Record
                                </>
                            )}
                        </Button>

                        <div className="space-y-3">
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Record will be saved to asset history</span>
                            </div>
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Activity will be logged</span>
                            </div>
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Approver will be notified</span>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ScheduleMaintenance;
